import {useState, useEffect, useRef} from "react"
import {Editor} from "@tinymce/tinymce-react"
import {useTranslation} from "react-i18next"
import useEcpAbstract from "../../swrCustomHooks/useEcpAbstract"
import useEcpSummary from "../../swrCustomHooks/useEcpSummary"
import useEcpKeypoints from "../../swrCustomHooks/useEcpKeypoints"
import useEcpGlossary from "../../swrCustomHooks/useEcpGlossary"
import useEcpVerbatims from "../../swrCustomHooks/useEcpVerbatims"
import EcpWorkflowNavigation from "../../components/ecp-workflow-navigation/EcpWorkflowNavigation"
import ValueSelector from "../../components/value-selector/ValueSelector"
import LoadingComponent from "../../components/loading-component/LoadingComponent"
import GlossaryTermCard from "../../components/glossary-term-card/GlossaryTermCard"
import axios from "axios"
import {API_ECP_PROJECTS} from "../../constants/apiUrls"
import {MEDIA_URL} from "../../constants/apiUrls"
import {toast} from "react-toastify"
import ParseKeypointsToHTML from "../../tools/ParseKeypointsToHTML"
import ParseVerbatimsToHTML from "../../tools/ParseVerbatimsToHTML"
import ParseKeypointsToJSON from "../../tools/ParseKeypointsToJSON"
import ParseVerbatimsToJSON from "../../tools/ParseVerbatimsToJSON"
import EcpContentSwitch from "../../tools/EcpContentSwitch"
import {useSessionContext} from "../../reactContext/contextCustomHooks/useSessionContext"
import {useTeamContext} from "../../reactContext/contextCustomHooks/useTeamContext"
import {useEcpWorkflowContext} from "../../reactContext/contextCustomHooks/useEcpWorkflowContext"
import useClickOutside from "../../tools/useClickOutside"
import isLicenceValidity from "../../tools/isLicenceValidity"
import ConfirmationModal from "../../components/confirmation-modal/ConfirmationModal"
import AddGlossaryTerm from "../../components/add-glossary-term/AddGlossaryTerm"

export default function EcpWorkflowRender({project, isPulling, setIsPulling}) {
    const {t} = useTranslation()
    const {sessionContext} = useSessionContext()
    const {teamContext} = useTeamContext()
    const {ecpWorkflowContext, setEcpWorkflowAction} = useEcpWorkflowContext()
    const [isValueSelector, setIsValueSelector] = useState(false)
    const [isHtmlPreview, setIsHtmlPreview] = useState(false)
    const [iframeSrc, setIframeSrc] = useState(null)
    const [EcpNavigationTab, setEcpNavigationTab] = useState("abstract")
    const [dirty, setDirty] = useState(false)
    const [editorKey, setEditorKey] = useState(0)
    const EcpNavigationTabRef = useRef(EcpNavigationTab)
    const editorRef = useRef(null)
    const {ecpAbstract, mutateEcpAbstract, isLoadingEcpAbstract, isErrorEcpAbstract} = useEcpAbstract(project.id)
    const {ecpSummary, mutateEcpSummary, isLoadingEcpSummary, isErrorEcpSummary} = useEcpSummary(project.id)
    const {ecpKeypoints, mutateEcpKeypoints, isLoadingEcpKeypoints, isErrorEcpKeypoints} = useEcpKeypoints(project.id)
    const {ecpVerbatims, mutateEcpVerbatims, isLoadingEcpVerbatims, isErrorEcpVerbatims} = useEcpVerbatims(project.id)
    const {ecpGlossary, mutateEcpGlossary,  isLoadingEcpGlossary, isErrorEcpGlossary} = useEcpGlossary(project.id)
    const ecpWorkflowRenderRef = useRef(null)
    const headersRequest = {headers: {Authorization: `Bearer ${sessionContext.idToken}`}}

    useClickOutside(ecpWorkflowRenderRef, () => {
        setIsValueSelector(false)
    })

    const getEditorInitialValue = () => {
        switch (EcpNavigationTab) {
            case "abstract":
                return ecpAbstract
            case "summary":
                return ecpSummary
            case "keypoints":
                return ParseKeypointsToHTML(ecpKeypoints)
            case "verbatims":
                return ParseVerbatimsToHTML(ecpVerbatims)
            default:
                return ecpAbstract
        }
    }

    const handleHtmlPreview = () => {
        setIsValueSelector(false)
        setEcpNavigationTab("abstract")
        EcpNavigationTabRef.current = "abstract"
        !isHtmlPreview && setIframeSrc(`${MEDIA_URL}/${project.id}/html/${project.lang_code}.html`)
        setIsHtmlPreview(!isHtmlPreview)
    }

    const handleEditorValue = (value) => {
        setIsValueSelector(false)
        setEcpNavigationTab(value)
        EcpNavigationTabRef.current = value
        switch (value) {
            case "abstract":
                editorRef?.current?.setContent(ecpAbstract)
                break
            case "summary":
                editorRef?.current?.setContent(ecpSummary)
                break
            case "keypoints":
                editorRef?.current?.setContent(ParseKeypointsToHTML(ecpKeypoints))
                break
            case "verbatims":
                editorRef?.current?.setContent(ParseVerbatimsToHTML(ecpVerbatims))
                break
            default:
                break
        }
    }

    const saveEditorContent = () => {
        return new Promise((resolve, reject) => {
            if (EcpNavigationTabRef.current === "glossary") {
                resolve()
            } else if (editorRef.current) {
                const content = EcpNavigationTabRef.current === "abstract" ? {abstract: editorRef.current.getContent()}
                : EcpNavigationTabRef.current === "summary" ? {summary: editorRef.current.getContent()}
                : EcpNavigationTabRef.current === "keypoints" ? ParseKeypointsToJSON(editorRef.current.getContent())
                : EcpNavigationTabRef.current === "verbatims" ? ParseVerbatimsToJSON(editorRef.current.getContent())
                : ""
                setDirty(false)
                editorRef.current.setDirty(false)
                axios.put(`${API_ECP_PROJECTS}/${project.id}/${EcpNavigationTabRef.current}`, content, headersRequest)
                .then(() => {
                    switch (EcpNavigationTabRef.current) {
                        case "abstract":
                            mutateEcpAbstract()
                            break
                        case "summary":
                            mutateEcpSummary()
                            break
                        case "keypoints":
                            mutateEcpKeypoints()
                            break
                        case "verbatims":
                            mutateEcpVerbatims()
                            break
                        default:
                            break
                    }
                    toast.success(t(`${EcpNavigationTabRef.current} is saved`))
                    resolve()
                })
                .catch((error) => {
                    console.error(`PUT /projects/${project.id}/${EcpNavigationTabRef.current}:`, error)
                    toast.error(t("An error has occurred"))
                    reject()
                })
            }
        })
    }

    const regenerate = (value) => {
        if (isLicenceValidity(teamContext.licenceStartDate, teamContext.licenceEndDate)) {
            axios.post(`${API_ECP_PROJECTS}/${project.id}/tries`, EcpContentSwitch(EcpNavigationTab, value), headersRequest)
            .then(() => {
                setIsPulling(true)
            })
            .catch((error) => {
                console.error(`POST /projects/${project.id}/tries: ${EcpContentSwitch(EcpNavigationTab, value)}`, error)
                toast.error(t("An error has occurred"))
            })
            setIsValueSelector(false)
        } else {
            toast.error(t("Licence expired. Please contact Neurowide sales department."))
        }
    }

    const handleClick = () => {
        if (isLicenceValidity(teamContext.licenceStartDate, teamContext.licenceEndDate)) {
            saveEditorContent().then(() => {
                setEcpWorkflowAction({type: "ECP_WORKFLOW_IS_CONFIRMATION_MODAL", payload: {isConfirmationModal: {modal: true, id: project.id, type: "ecp-export"}}})
            })
        } else {
            toast.error(t("Licence expired. Please contact Neurowide sales department."))
        }
    }

    const handleUpdate = () => {
        EcpNavigationTabRef.current = EcpNavigationTab
        if (editorRef.current) {
            setDirty(false)
            setEditorKey(prev => prev + 1)
            handleEditorValue("abstract")
        }
    }

    const deleteGlossaryTerm = (value) => {
        return new Promise((resolve, reject) => {
            if (value) {
                const headersRequest = {headers: {Authorization: `Bearer ${sessionContext.idToken}`}}
                let terms = ecpGlossary.glossary
                terms.splice(ecpWorkflowContext.isConfirmationModal.id, 1)
                axios.put(`${API_ECP_PROJECTS}/${project.id}/glossary`, {glossary: terms}, headersRequest)
                .then(() => {
                    mutateEcpGlossary()
                    toast.success(t("Term deleted!"))
                    resolve()
                })
                .catch((error) => {
                    console.error(`PUT ${API_ECP_PROJECTS}/${project.id}/glossary :` , error)
                    toast.error(t("An error has occurred"))
                    reject()
                })
            } else {
                resolve()
            }
        })
    }

    useEffect(() => {
        handleUpdate()
    }, [])

    useEffect(() => {
        mutateEcpAbstract()
        mutateEcpSummary()
        mutateEcpKeypoints()
        mutateEcpVerbatims()
        mutateEcpGlossary()
        handleUpdate()
    }, [isPulling])

    if (isErrorEcpAbstract || isLoadingEcpAbstract || ecpAbstract?.message ||
        isErrorEcpSummary || isLoadingEcpSummary || ecpSummary?.message ||
        isErrorEcpKeypoints || isLoadingEcpKeypoints || ecpKeypoints?.message ||
        isErrorEcpVerbatims || isLoadingEcpVerbatims || ecpVerbatims?.message ||
        isErrorEcpGlossary || isLoadingEcpGlossary || ecpGlossary?.message) return <LoadingComponent/>
    return (
        <>
        {ecpWorkflowContext.isConfirmationModal.type === "delete-glossary-term" && <ConfirmationModal type={"delete-glossary-term"} deleteGlossaryTerm={deleteGlossaryTerm} modalMessage={""}/>}
        <div ref={ecpWorkflowRenderRef} className="SECTION ECP-WORKFLOW-RENDER">
            {(isValueSelector && !isHtmlPreview) && <ValueSelector setIsValueSelector={setIsValueSelector} EcpNavigationTab={EcpNavigationTab} regenerate={regenerate} idToken={sessionContext.idToken} idProject={project.id}/>}
            <EcpWorkflowNavigation project={project} setIsValueSelector={setIsValueSelector} EcpNavigationTab={EcpNavigationTab} handleHtmlPreview={handleHtmlPreview} handleEditorValue={handleEditorValue} isHtmlPreview={isHtmlPreview}/>
            {isHtmlPreview ?
                <>
                <iframe src={iframeSrc} title="HTML API Preview" width="100%" height="500"/>
                </>
                :
                <>
                {EcpNavigationTabRef.current === "glossary" ?
                    <>
                    <ul className="glossary-term-list">
                        {ecpGlossary.glossary.map((term, index) => (
                            <GlossaryTermCard key={`term-${index}`} index={index} term={term} finished={project.finished}/>
                        ))}
                        {!project.finished && <AddGlossaryTerm/>}
                    </ul>
                    </>
                    :
                    <>
                    <Editor
                        tinymceScriptSrc={"/tinymce/tinymce.min.js"}
                        key={editorKey}
                        initialValue={getEditorInitialValue()}
                        onInit={(evt, editor) => editorRef.current = editor}
                        onDirty={() => setDirty(true)}
                        state={EcpNavigationTab}
                        init={{
                            license_key: "gpl",
                            height: 500,
                            menubar: false,
                            plugins: [
                                "charmap", "searchreplace", "wordcount"
                            ],
                            paste_data_images: false,
                            toolbar: "undo redo | bold italic | removeformat | searchreplace | save",
                            content_style: "body {font-family:Helvetica,Arial,sans-serif; font-size:14px}",
                            setup: (editor) => {
                                editor.ui.registry.addIcon("custom-save", `<svg class="custom-save" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#C30F0E"><path d="M840-680v480q0 33-23.5 56.5T760-120H200q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h480l160 160Zm-80 34L646-760H200v560h560v-446ZM480-240q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35ZM240-560h360v-160H240v160Zm-40-86v446-560 114Z"/></svg>`)
                                !project.finished && editor.ui.registry.addButton("save", {
                                    icon: "custom-save",
                                    tooltip: t("Save"),
                                    onAction: () => {
                                        if (isLicenceValidity(teamContext.licenceStartDate, teamContext.licenceEndDate)) {
                                            saveEditorContent()
                                        } else {
                                            toast.error(t("Licence expired. Please contact Neurowide sales department."))
                                        }
                                    }
                                })
                            }
                        }}
                    />
                    </>
                }
                </>
            }
            {!project.finished &&
                <div className="actions">
                    <button onClick={handleClick} title={t("Finalize")}>{t("Finalize")}</button>
                </div>
            }
        </div>
        </>
    )
}